import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button, Input, Select, Spinner } from '../atoms';
import { useToast } from '../organisms';

/**
 * FormBuilderPanel — GM4 · PDF Form Builder (rendered as a tab inside FormsLibrary).
 *
 * Distinct from the statutory {{token}} library: here the firm uploads a REAL
 * fillable PDF (an AcroForm), the backend extracts its field names via pdf-lib,
 * and the user maps each PDF field → a system data path. Then a per-client filled +
 * flattened PDF is generated as a document and delivered to the client portal.
 *
 * Fully wired to the REAL backend (src/routes/form-builder.js):
 *   • GET  /api/form-builder/data-paths                    → the fixed path menu
 *   • GET  /api/form-builder/templates                     → template list
 *   • POST /api/form-builder/templates {name,form_type,file_data} → upload + extract
 *   • GET  /api/form-builder/templates/:id                 → fields + current map
 *   • PUT  /api/form-builder/templates/:id/mapping         → save the map
 *   • POST /api/form-builder/templates/:id/generate {client_id} → fill → document
 *   • POST /api/form-builder/documents/:documentId/deliver → share to the portal
 */

interface DataPath { path: string; label: string }
interface TemplateSummary { id: string; name: string; form_type: string | null; mapped_field_count: number }
interface Mapping { pdf_field_name: string; data_path: string; label?: string }
interface TemplateDetail { id: string; name: string; form_type: string | null; pdf_fields: string[]; mappings: Mapping[] }
interface Client { id: string; name: string; short_name: string | null }
interface GenerateResult { document_id: string; filename: string; filled: string[]; unresolved: string[]; client_id: string }

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function normalizeStrings(data: unknown): string[] {
  if (Array.isArray(data)) return data as string[];
  if (data && Array.isArray((data as { values?: string[] }).values)) return (data as { values: string[] }).values;
  return [];
}

export function FormBuilderPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [mapEdits, setMapEdits] = useState<Record<string, string>>({});
  const [clientId, setClientId] = useState('');
  const [generated, setGenerated] = useState<GenerateResult | null>(null);

  const dataPathsQuery = useQuery<DataPath[]>({
    queryKey: ['form-builder', 'data-paths'],
    queryFn: ({ signal }) => api.get<DataPath[]>('/api/form-builder/data-paths', signal),
  });

  const formTypesQuery = useQuery<string[]>({
    queryKey: ['org-settings', 'form-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/form-types', signal).then(normalizeStrings),
  });

  const templatesQuery = useQuery<TemplateSummary[]>({
    queryKey: ['form-builder', 'templates'],
    queryFn: ({ signal }) => api.get<TemplateSummary[]>('/api/form-builder/templates', signal),
  });

  const clientsQuery = useQuery<Client[]>({
    queryKey: ['form-builder', 'clients'],
    queryFn: ({ signal }) => api.get<Client[]>('/api/clients', signal),
  });

  const detailQuery = useQuery<TemplateDetail>({
    queryKey: ['form-builder', 'template', selectedId],
    queryFn: ({ signal }) => api.get<TemplateDetail>(`/api/form-builder/templates/${selectedId}`, signal),
    enabled: !!selectedId,
  });

  // Seed the mapping editor from the persisted map whenever a template loads.
  useEffect(() => {
    if (!detailQuery.data) return;
    const seed: Record<string, string> = {};
    for (const f of detailQuery.data.pdf_fields) seed[f] = '';
    for (const m of detailQuery.data.mappings) seed[m.pdf_field_name] = m.data_path;
    setMapEdits(seed);
    setGenerated(null);
  }, [detailQuery.data]);

  const uploadMutation = useMutation<{ id: string }, Error, void>({
    mutationFn: async () => {
      const file_data = await readFileAsBase64(uploadFile!);
      return api.post<{ id: string }>('/api/form-builder/templates', {
        name: uploadName.trim(), form_type: uploadType || null, file_data, filename: uploadFile!.name,
      });
    },
    onSuccess: (data) => {
      toast({ variant: 'success', message: 'Template uploaded — fields extracted' });
      setUploadName(''); setUploadType(''); setUploadFile(null);
      qc.invalidateQueries({ queryKey: ['form-builder', 'templates'] });
      setSelectedId(data.id);
    },
    onError: (e) => toast({ variant: 'error', message: `Upload failed: ${e.message}` }),
  });

  const saveMappingMutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.put(`/api/form-builder/templates/${selectedId}/mapping`, {
      mappings: Object.entries(mapEdits)
        .filter(([, path]) => !!path)
        .map(([pdf_field_name, data_path]) => ({ pdf_field_name, data_path })),
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Field map saved' });
      qc.invalidateQueries({ queryKey: ['form-builder', 'template', selectedId] });
      qc.invalidateQueries({ queryKey: ['form-builder', 'templates'] });
    },
    onError: (e) => toast({ variant: 'error', message: `Save failed: ${e.message}` }),
  });

  const generateMutation = useMutation<GenerateResult, Error, void>({
    mutationFn: () => api.post<GenerateResult>(`/api/form-builder/templates/${selectedId}/generate`, { client_id: clientId }),
    onSuccess: (data) => {
      setGenerated(data);
      toast({ variant: 'success', message: `Generated ${data.filename}` });
    },
    onError: (e) => toast({ variant: 'error', message: `Generation failed: ${e.message}` }),
  });

  const deliverMutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/form-builder/documents/${generated!.document_id}/deliver`, {}),
    onSuccess: () => toast({ variant: 'success', message: 'Delivered to the client portal' }),
    onError: (e) => toast({ variant: 'error', message: `Delivery failed: ${e.message}` }),
  });

  const pathOptions = useMemo(() => [
    { value: '', label: '— Unmapped —' },
    ...(dataPathsQuery.data ?? []).map(d => ({ value: d.path, label: `${d.label} (${d.path})` })),
  ], [dataPathsQuery.data]);

  const typeOptions = useMemo(() => [
    { value: '', label: 'No type' },
    ...(formTypesQuery.data ?? []).map(t => ({ value: t, label: t })),
  ], [formTypesQuery.data]);

  const clientOptions = useMemo(() => [
    { value: '', label: 'Select a client…' },
    ...(clientsQuery.data ?? []).map(c => ({ value: c.id, label: c.short_name ? `${c.name} (${c.short_name})` : c.name })),
  ], [clientsQuery.data]);

  const canUpload = uploadName.trim().length > 0 && !!uploadFile;
  const mappedCount = Object.values(mapEdits).filter(Boolean).length;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(min(100%, 280px), 1fr) minmax(340px, 1.4fr)', gap: 'var(--sp-5)', alignItems: 'start' }}>
      {/* Left — upload + template list */}
      <section>
        <h3 style={sectionHeadStyle}>Upload a PDF template</h3>
        <div style={cardStyle}>
          <label style={fieldLabelWrap}>
            <span style={filterLabelStyle}>Template name</span>
            <Input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. Appointment of Agent" />
          </label>
          <label style={fieldLabelWrap}>
            <span style={filterLabelStyle}>Form type</span>
            <Select value={uploadType} onChange={e => setUploadType(e.target.value)} options={typeOptions} />
          </label>
          <label style={fieldLabelWrap}>
            <span style={filterLabelStyle}>PDF file (fillable / AcroForm)</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}
            />
          </label>
          <Button
            variant="primary"
            disabled={!canUpload || uploadMutation.isPending}
            onClick={() => uploadMutation.mutate()}
            style={{ marginTop: 'var(--sp-3)' }}
          >
            {uploadMutation.isPending ? 'Uploading…' : 'Upload & extract fields'}
          </Button>
        </div>

        <h3 style={{ ...sectionHeadStyle, marginTop: 'var(--sp-5)' }}>Templates</h3>
        {templatesQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}><Spinner size="sm" /> Loading…</p>}
        {templatesQuery.data && templatesQuery.data.length === 0 && (
          <p style={{ color: 'var(--color-ink-3)' }}>No PDF templates yet. Upload one above.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {(templatesQuery.data ?? []).map(t => {
            const active = t.id === selectedId;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                style={{
                  textAlign: 'left', cursor: 'pointer', width: '100%',
                  background: active ? 'var(--color-celadon-bg)' : 'var(--color-card)',
                  border: `1px solid ${active ? 'var(--color-hunter)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--r-lg)', padding: 'var(--sp-3) var(--sp-4)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>{t.name}</span>
                  <Badge variant={t.mapped_field_count > 0 ? 'success' : 'neutral'}>{t.mapped_field_count} mapped</Badge>
                </div>
                {t.form_type && (
                  <div style={{ marginTop: 'var(--sp-2)' }}>
                    <Badge variant="info">{t.form_type}</Badge>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Right — mapping + generate + deliver */}
      <section>
        <h3 style={sectionHeadStyle}>Map fields &amp; generate</h3>
        <div style={cardStyle}>
          {!selectedId ? (
            <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Select a template to map its fields.</p>
          ) : detailQuery.isLoading ? (
            <p style={{ color: 'var(--color-ink-3)', margin: 0 }}><Spinner size="sm" /> Loading fields…</p>
          ) : detailQuery.data ? (
            <>
              <div style={{ marginBottom: 'var(--sp-4)' }}>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)' }}>Template</div>
                <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{detailQuery.data.name}</div>
              </div>

              {/* Field → data-path map */}
              {detailQuery.data.pdf_fields.length === 0 ? (
                <p style={{ color: 'var(--color-ink-3)' }}>This PDF has no fillable AcroForm fields.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                  {detailQuery.data.pdf_fields.map(field => (
                    <div key={field} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <code style={{
                        fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-1) var(--sp-2)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{field}</code>
                      <Select
                        value={mapEdits[field] ?? ''}
                        onChange={e => setMapEdits(prev => ({ ...prev, [field]: e.target.value }))}
                        options={pathOptions}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' }}>
                <Button variant="secondary" disabled={saveMappingMutation.isPending} onClick={() => saveMappingMutation.mutate()}>
                  {saveMappingMutation.isPending ? 'Saving…' : 'Save field map'}
                </Button>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{mappedCount} of {detailQuery.data.pdf_fields.length} mapped</span>
              </div>

              {/* Generate for a client */}
              <div style={{ marginTop: 'var(--sp-5)', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--color-border)' }}>
                <label style={fieldLabelWrap}>
                  <span style={filterLabelStyle}>Generate for business</span>
                  <Select value={clientId} onChange={e => setClientId(e.target.value)} options={clientOptions} />
                </label>
                <Button
                  variant="primary"
                  disabled={!clientId || generateMutation.isPending}
                  onClick={() => generateMutation.mutate()}
                  style={{ marginTop: 'var(--sp-2)' }}
                >
                  {generateMutation.isPending ? 'Generating…' : 'Generate filled PDF'}
                </Button>
              </div>

              {/* Generated result — download + deliver */}
              {generated && (
                <div style={{
                  marginTop: 'var(--sp-4)', padding: 'var(--sp-3)', borderRadius: 'var(--r-md)',
                  background: 'var(--color-status-green-bg)', border: '1px solid var(--color-status-green)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>{generated.filename}</span>
                    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                      <Button variant="secondary" size="sm" onClick={() => downloadDocument(generated.document_id, (m) => toast({ variant: 'error', message: m }))}>
                        Download
                      </Button>
                      <Button variant="primary" size="sm" disabled={deliverMutation.isPending} onClick={() => deliverMutation.mutate()}>
                        {deliverMutation.isPending ? 'Delivering…' : 'Deliver to portal'}
                      </Button>
                    </div>
                  </div>
                  <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                    {generated.filled.length} field(s) filled
                    {generated.unresolved.length > 0 ? ` · ${generated.unresolved.length} had no data: ${generated.unresolved.join(', ')}` : ''}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p style={{ color: 'var(--color-status-red)', margin: 0 }}>Failed to load the template.</p>
          )}
        </div>
      </section>
    </div>
  );
}

const filterLabelStyle: React.CSSProperties = {
  fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
  letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
};

const sectionHeadStyle: React.CSSProperties = {
  margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
  textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--color-card)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
};

const fieldLabelWrap: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginBottom: 'var(--sp-3)',
};
